/**
 * メッシュグラデーションの描画エンジン。
 *
 * 低解像度のキャンバスに radial gradient のブロブを重ね、CSS 側の blur で
 * 拡大してメッシュグラデーションにする。WebGL を導入せずに滑らかな結果が
 * 得られ、1 枚あたりの描画コストが小さいので一覧に多数並べても耐える。
 *
 * 地色は塗らず透明のまま残す。白基調のガラス板を下に敷き、その上に色の雲が
 * 浮いて見える構成にするため、最後に円形マスクを掛けて縁を白へ抜く。
 */

import { AudioCategory } from '../../../audio-preview/core/audioCategory';
import { DriveSignal, MotionPreset } from './audioDriver';
import { AudioThumbnailPalette, Rgb, createRandom, hashSeed, rgba } from './audioThumbnailPalette';

/**
 * キャンバスの内部解像度（px）。CSS で拡大するので小さくてよいが、
 * 低すぎると拡大時のガタを隠すために強い blur が必要になり、
 * 全体が霞んでガラスの硬さが出ない。
 */
export const MESH_CANVAS_SIZE = 128;

/** 描画するブロブ数 */
const BLOB_COUNT = 5;

/** ブロブが反応する擬似帯域 */
type BlobBand = 'level' | 'low' | 'mid' | 'high';

interface MeshBlob {
  /** 基準位置（0-1 の正規化座標） */
  baseX: number;
  baseY: number;
  /** 公転の半径（正規化） */
  orbitX: number;
  orbitY: number;
  /** 公転の角速度（rad/s）と初期位相 */
  speed: number;
  phase: number;
  /** 基準の半径（正規化） */
  radius: number;
  color: Rgb;
  alpha: number;
  band: BlobBand;
  /**
   * ビートで膨らむか縮むか。全ブロブが同時に膨らむと画面が均一に混ざって
   * メッシュの構造が飛ぶため、一部を逆位相にして「脈打つ」表現にする。
   */
  swellSign: 1 | -1;
}

export interface MeshScene {
  blobs: MeshBlob[];
  /** 光沢ハイライトの位置（正規化） */
  specularX: number;
  specularY: number;
  glow: Rgb;
  /**
   * 色を収める円形マスク。これより外は白（透明）に抜けるため、
   * タイルの四隅にガラス板の地色が残る。
   */
  maskX: number;
  maskY: number;
  maskRadius: number;
  /**
   * 雲全体を中心まわりに回す角速度（rad/s）。個々の公転だけでは
   * 「その場で揺れている」印象に留まるため、全体の旋回を足す。
   */
  swirlSpeed: number;
}

/**
 * カテゴリごとにブロブへ割り当てる帯域。
 * MUSIC は全体が同期して脈打ち、SoundEffects は帯域ごとに独立して動き、
 * VOICE は音量に一体で追従する。
 */
const BAND_ASSIGNMENT: Record<AudioCategory, BlobBand[]> = {
  music: ['level', 'low', 'level', 'mid', 'low'],
  sound_effect: ['low', 'mid', 'high', 'mid', 'high'],
  voice: ['level', 'level', 'low', 'level', 'mid'],
  // 不明なので性格付けはしないが、全ブロブが同じ帯域だと一体で膨らむだけの
  // 単調な動きになる。緩い分散に留める
  neutral: ['level', 'low', 'level', 'low', 'mid'],
};

/**
 * シードからブロブ配置を決める。同じシードなら常に同じ絵になり、
 * シードが違えば同カテゴリでも配置・位相・回転方向が変わる。
 */
export function createMeshScene(
  palette: AudioThumbnailPalette,
  category: AudioCategory,
  seed: string,
): MeshScene {
  const random = createRandom(hashSeed(`${seed}:mesh`));
  const bands = BAND_ASSIGNMENT[category];
  // 芯以外に 3 色を巡回で配る。1 枚の中で色相が転ぶ虹彩ガラスの見え方になる
  const hues = [palette.base, palette.accent, palette.iris];

  const blobs: MeshBlob[] = Array.from({ length: BLOB_COUNT }, (_, i) => {
    // ブロブを円周上に散らしてから揺らす。重なりすぎを避けつつ配置に個体差を出す
    const angle = (i / BLOB_COUNT) * Math.PI * 2 + random() * 0.9;
    const direction = random() < 0.5 ? -1 : 1;
    // 最後の 1 つは雲の芯。中心寄りに小さく濃く置いて、周囲の淡い色との
    // 濃淡差を作る（全ブロブが同じ濃さだと平坦な霞になる）
    const isCore = i === BLOB_COUNT - 1;
    const distance = isCore ? random() * 0.08 : 0.1 + random() * 0.17;

    return {
      baseX: 0.5 + Math.cos(angle) * distance,
      baseY: 0.5 + Math.sin(angle) * distance,
      // 公転 1 周が 8-20 秒程度になる速度と、タイル幅の 1-2 割を動く半径。
      // これより小さいと「滑らかに動いている」ことが知覚できない
      orbitX: 0.08 + random() * 0.1,
      orbitY: 0.08 + random() * 0.1,
      speed: direction * (0.32 + random() * 0.46),
      phase: random() * Math.PI * 2,
      // 大きすぎると全面が均一に混ざってメッシュの構造が消える
      radius: isCore ? 0.14 + random() * 0.14 : 0.18 + random() * 0.2,
      color: isCore ? palette.deep : hues[i % hues.length],
      // 透明な色ガラスを重ねる想定なので 1 枚あたりは薄い。
      // 重なった部分だけが濃く色が転び、そこがガラスらしさになる
      alpha: isCore ? 0.72 + random() * 0.16 : 0.34 + random() * 0.18,
      band: bands[i],
      swellSign: i % 3 === 2 ? -1 : 1,
    };
  });

  return {
    blobs,
    specularX: 0.24 + random() * 0.16,
    specularY: 0.18 + random() * 0.14,
    glow: palette.glow,
    // 中心を僅かにずらすと、並んだときに色の寄り方で個体差が出る
    maskX: 0.5 + (random() - 0.5) * 0.16,
    maskY: 0.5 + (random() - 0.5) * 0.16,
    // 色の占める面積そのものを散らす。小さく凝った玉と、板いっぱいに
    // 広がった雲が混ざることで、色相以外にも個体差の手がかりが増える
    maskRadius: 0.44 + random() * 0.26,
    swirlSpeed: (random() < 0.5 ? -1 : 1) * (0.06 + random() * 0.12),
  };
}

/**
 * 1 フレーム描画する。
 * @param timeSec アニメーション開始からの経過秒（再生位置ではない）
 */
export function renderMeshFrame(
  ctx: CanvasRenderingContext2D,
  scene: MeshScene,
  timeSec: number,
  drive: DriveSignal,
  preset: MotionPreset,
): void {
  const size = MESH_CANVAS_SIZE;

  // 地色は塗らない。透明のまま残して下のガラス板（白）を透かす
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, size, size);

  // 雲全体の旋回。音が大きいほど速く回る
  const swirl = timeSec * scene.swirlSpeed * preset.driftSpeed * (1 + drive.low * preset.swirlGain);
  const swirlCos = Math.cos(swirl);
  const swirlSin = Math.sin(swirl);

  scene.blobs.forEach((blob, index) => {
    const drift = timeSec * blob.speed * preset.driftSpeed + blob.phase;
    // 音に反応して公転半径そのものが広がる
    const orbitScale = 1 + drive.level * preset.orbitGain;

    // 基準位置を中心まわりに回してから公転を足す
    const offsetX = blob.baseX - 0.5;
    const offsetY = blob.baseY - 0.5;
    // ビートで外側へ弾く。全ブロブを同時に弾くと単なる拡大になるので符号を分ける
    const kick = 1 + blob.swellSign * drive.beat * preset.beatKick;
    const rotatedX = (offsetX * swirlCos - offsetY * swirlSin) * kick;
    const rotatedY = (offsetX * swirlSin + offsetY * swirlCos) * kick;

    const x = (0.5 + rotatedX + Math.cos(drift) * blob.orbitX * orbitScale) * size;
    const y = (0.5 + rotatedY + Math.sin(drift * 1.3) * blob.orbitY * orbitScale) * size;

    const bandValue = drive[blob.band];
    const swell = 1 + blob.swellSign * drive.beat * preset.beatGain;
    const radius =
      blob.radius *
      size *
      Math.max(0.1, swell) *
      (1 + drive.level * preset.levelGain + bandValue * preset.bandGain);

    if (radius <= 0) return;

    // 1 枚目は普通に置き、以降は乗算で重ねる。透明な色ガラスを重ねたときと
    // 同じ減法混色になり、重なりで色が転んで奥行きが出る
    ctx.globalCompositeOperation = index === 0 ? 'source-over' : 'multiply';

    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, rgba(blob.color, blob.alpha));
    gradient.addColorStop(0.55, rgba(blob.color, blob.alpha * 0.42));
    gradient.addColorStop(1, rgba(blob.color, 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  });

  // 光沢。速い成分に反応させると「艶が走る」印象になる
  const specularStrength = 0.1 + drive.high * 0.22 + drive.beat * 0.12;
  const sx = scene.specularX * size;
  const sy = scene.specularY * size;
  const sr = size * (0.34 + drive.high * 0.12);
  const specular = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
  specular.addColorStop(0, rgba(scene.glow, specularStrength));
  specular.addColorStop(1, rgba(scene.glow, 0));
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = specular;
  ctx.fillRect(0, 0, size, size);

  // 円形マスクで縁を抜く。色が全面を埋めず、四隅にガラス板の白が残る
  const mx = scene.maskX * size;
  const my = scene.maskY * size;
  const mr = scene.maskRadius * size * (1 + drive.level * preset.orbitGain * 0.4);
  const mask = ctx.createRadialGradient(mx, my, 0, mx, my, mr);
  mask.addColorStop(0, 'rgba(0, 0, 0, 1)');
  mask.addColorStop(0.5, 'rgba(0, 0, 0, 0.92)');
  mask.addColorStop(0.8, 'rgba(0, 0, 0, 0.4)');
  mask.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = mask;
  ctx.fillRect(0, 0, size, size);

  ctx.globalCompositeOperation = 'source-over';
}

// =============================================================================
// 共有 rAF ループ
// =============================================================================

/**
 * サムネイル 1 枚ごとに rAF を回すと Gallery 一覧のような多数配置で
 * コールバックが増えすぎるため、1 本のループに集約する。
 */
type MeshTicker = (_nowMs: number) => void;

const tickers = new Set<MeshTicker>();
let rafId: number | null = null;

function tick(nowMs: number): void {
  // 実行中に register / unregister されても安全なようコピーしてから回す
  for (const ticker of Array.from(tickers)) {
    // 1 枚の描画が投げてもループごと落とさない。落とすと rafId が非 null で
    // 残るため再登録も効かず、同じページの全サムネイルが永久に止まる
    try {
      ticker(nowMs);
    } catch (error) {
      tickers.delete(ticker);
      console.error('[audioThumbnail] mesh ticker failed and was detached', error);
    }
  }
  rafId = tickers.size > 0 ? requestAnimationFrame(tick) : null;
}

/** ティッカーを共有ループに登録する。戻り値を呼ぶと解除される */
export function registerMeshTicker(ticker: MeshTicker): () => void {
  tickers.add(ticker);
  if (rafId === null) rafId = requestAnimationFrame(tick);

  return () => {
    tickers.delete(ticker);
    if (tickers.size === 0 && rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
}
